import { useState, useEffect, useRef, useMemo,useCallback } from "react";
import "./MyClients.scss";
import {
  BiChevronDown,
  BiPhone,
  BiMail,
  BiBuilding,
  BiMap,
  BiBriefcase,
  BiBriefcaseAlt,
  BiMapAlt,
  BiBuildingHouse,
  BiMailSend,
  BiPhoneCall,
} from "react-icons/bi";
import { useLoading } from "../../context/LoadingContext";
import { useToast } from "../../context/ToastContext";
import { deleteMyClient, getMyClients } from "../../services/api/MyClientsApi";
import { useNavigate } from "react-router-dom";
import DeleteConfirmationModal from "../../components/DeleteConfirmationModal/DeleteConfirmationModal";
import { useSearchResults } from "../../context/SearchResultsContext";
import { IoGrid, IoGridOutline } from "react-icons/io5";
import { FaTableList } from "react-icons/fa6";
import { CiViewTable } from "react-icons/ci";
import { useClientSelection } from "../../context/ClientSelectionContext";
import { formatText } from "../../components/TruncateParties";

export const MyClients = () => {
  const navigate = useNavigate();
  const { searchResults, searchType } = useSearchResults();
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [clientToDelete, setClientToDelete] = useState(null);

  const [viewMode, setViewMode] = useState("table");

  const dropdownRefs = useRef([]);
  const [clients, setClients] = useState([]);
  const [openDropdown, setOpenDropdown] = useState(null);
  const { showLoading, hideLoading } = useLoading();
  const { showToast } = useToast();

  const {
    searchTerm: clientSearchTerm,
    setSearchTerm: setClientSearchTerm,
    filters: clientFilters,
    setFilters: setClientFilters,
    filteredClients,
    setFilteredClients,
    hasActiveFilters: hasClientFilters,
    clearAllFilters: clearClientFilters,
    triggerClientRefresh,
    pagination,
    updatePaginationInfo,
    setPagination

  } = useClientSelection();

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        openDropdown !== null &&
        dropdownRefs.current[openDropdown] &&
        !dropdownRefs.current[openDropdown].contains(e.target)
      ) {
        setOpenDropdown(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [openDropdown]);

  const toggleDropdown = (index) => {
    setOpenDropdown(openDropdown === index ? null : index);
  };

  const handleClientsPerPageChange = useCallback((e) => {
    const newClientsPerPage = Number(e.target.value);
    setPagination(prev => ({
      ...prev,
      limit: newClientsPerPage,
      page: 0 
    }));
  }, [setPagination]);

  const handlePageChange = useCallback((newPage) => {
    setPagination(prev => ({
      ...prev,
      page: newPage
    }));
  }, [setPagination]);

  const displayMyClients = useMemo(() => {
    // Use filteredClients from context for unified behavior
    console.log(
      "Using filteredClients from context:",
      filteredClients?.length || 0
    );
    return filteredClients || [];
  }, [filteredClients]);

  const isInSearchMode = searchType === "clients";

  const handleAction = async (action, client) => {
    if (action === "Update") {
      navigate(`/home/my-clients/edit/${client._id}`, { state: client });
    } else if (action === "Delete") {
      setClientToDelete(client);
      setIsDeleteModalOpen(true);
    } else {
      showToast(`${action} for ${client.salutation} ${client.name}`, {
        type: "success",
      });
    }

    setOpenDropdown(null); // Close dropdown after action
  };
  const confirmDelete = async () => {
    if (!clientToDelete) return;

    try {
      await deleteMyClient(clientToDelete._id);
      showToast("Client deleted successfully", { type: "success" });
      // setClients((prev) => prev.filter((c) => c._id !== clientToDelete._id));
      // triggerClientRefresh(); // Notify context to refresh clients
      if (filteredClients.length === 1 && pagination.page > 0) {
        handlePageChange(pagination.page - 1);
      } else {
        triggerClientRefresh(); // Otherwise, just refresh the current page
      }
    } catch (err) {
      console.error("Delete failed:", err);
      showToast("Failed to delete client. Please try again.", {
        type: "error",
      });
    } finally {
      setIsDeleteModalOpen(false);
      setClientToDelete(null);
    }
  };

  const cancelDelete = () => {
    setIsDeleteModalOpen(false);
    setClientToDelete(null);
  };

  const [isFilterOpen, setIsFilterOpen] = useState(false);

  const finalDisplayClients = useMemo(() => {
    // Apply any additional UI-only filters if needed
    // For now, use displayMyClients directly since context handles filtering
    return displayMyClients;
  }, [displayMyClients]);

  // REPLACE hasActiveFilters function:
  const hasActiveFilters = () => {
    return hasClientFilters;
  };

  // UPDATE getNoDataMessage:
  const getNoDataMessage = () => {
    if (hasClientFilters) {
      return "No clients found matching your search and filters.";
    }
    return "No Clients found. Start by creating a new one!";
  };

  // UPDATE shouldShowNoDataMessage:
  const shouldShowNoDataMessage = () => {
    return displayMyClients.length === 0;
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (!e.target.closest(".filter-dropdown-wrapper")) {
        setIsFilterOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const filterButtonRef = useRef();
  const filterDropdownRef = useRef();
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  const [dropdownOverflow, setDropdownOverflow] = useState("auto");

  useEffect(() => {
    if (isFilterOpen && filterButtonRef.current) {
      const buttonRect = filterButtonRef.current.getBoundingClientRect();
      const dropdownWidth = 365;
      const dropdownMargin = 4;
      let leftPosition = buttonRect.left;
      if (leftPosition + dropdownWidth > window.innerWidth) {
        leftPosition = buttonRect.right - dropdownWidth;
      }
      if (leftPosition < 0) {
        leftPosition = 10;
      }
      // Always show below the button, but limit maxHeight if needed
      setDropdownPosition({
        top: buttonRect.bottom + dropdownMargin,
        left: leftPosition,
        maxHeight: 300,
      });
    }

    const handleScroll = (e) => {
      if (!isFilterOpen) return;
      // Only close if scroll is outside the dropdown
      const dropdown = document.querySelector(".filter-dropdown-box");
      if (dropdown && e && e.target && dropdown.contains(e.target)) {
        // Scrolling inside the dropdown, do nothing
        return;
      }
      setIsFilterOpen(false);
    };

    if (isFilterOpen) {
      window.addEventListener("scroll", handleScroll, true);
    }

    return () => {
      window.removeEventListener("scroll", handleScroll, true);
    };
  }, [isFilterOpen]);

  // Check if dropdown content is overflowing and set overflowY accordingly
  useEffect(() => {
    if (isFilterOpen && filterDropdownRef.current) {
      const el = filterDropdownRef.current;
      if (el.scrollHeight > el.clientHeight) {
        setDropdownOverflow("auto");
      } else {
        setDropdownOverflow("visible");
      }
    }
  }, [isFilterOpen, dropdownPosition]);

  return (
    <div className={`my-clients-wrapper`}>
      <div className="invoices-header">
        <div className="view-toggle-wrapper">
          <button
            className={`view-toggle-btn ${viewMode === "grid" ? "active" : ""}`}
            onClick={() => setViewMode("grid")}
          >
            <IoGrid />
          </button>
          <button
            className={`view-toggle-btn ${viewMode === "table" ? "active" : ""
              }`}
            onClick={() => setViewMode("table")}
          >
            <FaTableList />
          </button>
          <div className="common-pagination-settings">
            <label htmlFor="clientPerPageSelect">Clients per page:</label>
            <select
              id="clientPerPageSelect"
              value={pagination.limit}
              onChange={handleClientsPerPageChange}
            >
              {[5, 10, 15, 20, 25, 50].map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="filter-dropdown-wrapper">
          <button
            className="filter-toggle-btn"
            onClick={() => setIsFilterOpen((prev) => !prev)}
            ref={filterButtonRef}
          >
            {isFilterOpen ? "Hide Filters" : "Show Filters"}
          </button>
          {isFilterOpen &&
            dropdownPosition.top !== 0 &&
            dropdownPosition.left !== 0 && (
              <div
                className="filter-dropdown-box"
                ref={filterDropdownRef}
                style={{
                  top: `${dropdownPosition.top}px`,
                  left: `${dropdownPosition.left}px`,
                  maxHeight: dropdownPosition.maxHeight
                    ? `${dropdownPosition.maxHeight}px`
                    : "500px",
                  overflowY: dropdownOverflow,
                  position: "fixed",
                }}
              >
                <div className="filter-group">
                  <label>Client Name</label>
                  <input
                    type="text"
                    value={clientFilters.clientName}
                    onChange={(e) =>
                      setClientFilters({
                        ...clientFilters,
                        clientName: e.target.value,
                      })
                    }
                    placeholder="e.g. Mr. John"
                  />
                </div>
                <div className="filter-group">
                  <label>Company Name</label>
                  <input
                    type="text"
                    value={clientFilters.company}
                    onChange={(e) => setClientFilters({ ...clientFilters, company: e.target.value })}

                    placeholder="e.g. Google"
                  />
                </div>
                <div className="filter-group">
                  <label>Email</label>
                  <input
                    type="email"
                    value={clientFilters.email}
                    onChange={(e) => setClientFilters({ ...clientFilters, email: e.target.value })}
                    placeholder="example@domain.com"
                  />
                </div>
                <div className="filter-group">
                  <label>GSTIN</label>
                  <input
                    type="text"
                    value={clientFilters.gstin}
                    onChange={(e) => setClientFilters({ ...clientFilters, gstin: e.target.value })}
                    placeholder="GSTIN"
                  />
                </div>

                <div className="filter-actions">
                  <button
                    className="clear-btn"
                    onClick={() => setClientFilters({ clientName: "", company: "", email: "", gstin: "" })}
                  >
                    Clear
                  </button>
                  <button
                    className="hide-btn"
                    onClick={() => setIsFilterOpen(false)}
                  >
                    Hide
                  </button>
                </div>
              </div>
            )}
        </div>
      </div>


      {hasClientFilters && (
        <div className="applied-filters">
          <span className="filters-text">Applied Filters:</span>

          {clientSearchTerm && clientSearchTerm.trim() && (
            <span
              className="filter-tag"
              onClick={() => {
                setClientSearchTerm('');
                // Clear DetailSection's search input
                if (window.clearDetailSectionSearch) {
                  window.clearDetailSectionSearch();
                }
              }}
            >
              Search: {formatText(clientSearchTerm)} ×
            </span>
          )}

          {/* Individual filter pills */}
          {clientFilters.clientName && (
            <span
              className="filter-tag"
              onClick={() => setClientFilters({ ...clientFilters, clientName: '' })}
            >
              Name: {formatText(clientFilters.clientName)} ×
            </span>
          )}

          {clientFilters.company && (
            <span
              className="filter-tag"
              onClick={() => setClientFilters({ ...clientFilters, company: '' })}
            >
              Company: {formatText(clientFilters.company)} ×
            </span>
          )}

          {clientFilters.email && (
            <span
              className="filter-tag"
              onClick={() => setClientFilters({ ...clientFilters, email: '' })}
            >
              Email: {formatText(clientFilters.email)} ×
            </span>
          )}

          {clientFilters.gstin && (
            <span
              className="filter-tag"
              onClick={() => setClientFilters({ ...clientFilters, gstin: '' })}
            >
              GSTIN: {formatText(clientFilters.gstin)} ×
            </span>
          )}

          <span
            className="filter-tag clear-all-tag"
            onClick={() => {
              clearClientFilters();
              // Clear DetailSection's search input  
              if (window.clearDetailSectionSearch) {
                window.clearDetailSectionSearch();
              }
            }}
          >
            Clear All
          </span>
        </div>
      )}
      <div className="clients-container">
        {shouldShowNoDataMessage() ? (
          <p className="no-clients-message">{getNoDataMessage()}</p>
        ) : viewMode === "grid" ? (
          finalDisplayClients.map((client, index) => (
            <div
              key={index}
              className="client-card-main"
              onClick={() =>
                navigate(`/home/my-clients/viewmyclient/${client._id}`)
              }
              style={{ cursor: "pointer" }}
            >
              <div className="client-header">
                <h2 className="client-name">
                  {client.salutation} {client.name}
                </h2>
                <div
                  className="client-actions"
                  ref={(el) => (dropdownRefs.current[index] = el)}
                >
                  <button
                    className="client-action-button"
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleDropdown(index);
                    }}
                  >
                    Actions <BiChevronDown />
                  </button>
                  {openDropdown === index && (
                    <ul className="dropdown-menu">
                      {/* <li className="view" onClick={(e) =>{e.stopPropagation(); handleAction("View Profile", client)}}>View Profile</li> */}
                      <li
                        className="update"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleAction("Update", client);
                        }}
                      >
                        Update Data
                      </li>
                      <li
                        className="delete"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleAction("Delete", client);
                        }}
                      >
                        Delete Client
                      </li>
                      {/* <li
                        className="message"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleAction("Send Message", client);
                        }}
                      >
                        Send Message
                      </li> */}
                    </ul>
                  )}
                </div>
              </div>

              <div className="client-details-grid">
                <div className="detail-item">
                  <BiBuildingHouse className="detail-icon" />
                  <strong>Company:</strong>{" "}
                  <span className="client-detail">
                    {client.company || "N/A"}
                  </span>
                </div>
                <div className="detail-item">
                  <BiPhoneCall className="detail-icon" />
                  <strong>Contact:</strong>{" "}
                  <span className="client-detail">{client.phno}</span>
                </div>
                {client.alternateContact && (
                  <div className="detail-item">
                    <BiPhoneCall className="detail-icon" />
                    <strong>Alt. Contact:</strong>{" "}
                    <span className="client-detail">
                      {client.alternateContact}
                    </span>
                  </div>
                )}

                {client.gstin && (
                  <div className="detail-item">
                    <BiBuildingHouse className="detail-icon" />
                    <strong>GSTIN:</strong>{" "}
                    <span className="client-detail">{client.gstin}</span>
                  </div>
                )}
                <div className="detail-item">
                  <BiMailSend className="detail-icon" />
                  <strong>Email:</strong>{" "}
                  <span className="client-detail">{client.email}</span>
                </div>
              </div>

              <div className="client-address">
                <h3 className="section-title">
                  <BiMapAlt className="section-icon" /> Address Information
                </h3>
                <p>
                  <span className="client-detail">
                    {client.addressLine1} {client.addressLine2}
                  </span>
                  <br />
                  <span className="client-detail">
                    {client.city} {client.state} {client.country}-
                    {client.zipcode}
                  </span>
                  <br />
                </p>
              </div>
            </div>
          ))
        ) : (
          <table className="clients-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Company</th>
                <th>Email</th>
                <th>Phone</th>
                <th>GSTIN</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {console.log(finalDisplayClients, "finalDisplayClients")}
              {finalDisplayClients.map((client, index) => (
                <tr
                  key={index}
                  onClick={() =>
                    navigate(`/home/my-clients/viewmyclient/${client._id}`)
                  }
                  style={{ cursor: "pointer" }}
                >
                  <td>
                    {client.salutation} {client.name}
                  </td>
                  <td>{client.company || "N/A"}</td>
                  <td>{client.email}</td>
                  <td>{client.phno}</td>
                  <td>{client.gstin || "N/A"}</td>
                  <td>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleAction("Update", client);
                      }}
                    >
                      Update
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleAction("Delete", client);
                      }}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          
        )}
        

        <DeleteConfirmationModal
          isOpen={isDeleteModalOpen}
          itemType={`Client ${clientToDelete?.salutation} ${clientToDelete?.name}`}
          onConfirm={confirmDelete}
          onCancel={cancelDelete}
        />

        
      </div>


      {(pagination.totalClients > pagination.limit || pagination.page > 0) && (
        <div className="pagination-controls">
          <button
            disabled={pagination.page === 0}
            onClick={() => handlePageChange(0)}
          >
            First
          </button>
          <button
            disabled={pagination.page === 0}
            onClick={() => handlePageChange(pagination.page - 1)}
          >
            Previous
          </button>
          <span>
            Page {pagination.page + 1} of {Math.ceil(pagination.totalClients / pagination.limit) || 1}
          </span>
          <button
            disabled={pagination.page >= Math.ceil(pagination.totalClients / pagination.limit) - 1}
            onClick={() => handlePageChange(pagination.page + 1)}
          >
            Next
          </button>
          <button
            disabled={pagination.page >= Math.ceil(pagination.totalClients / pagination.limit) - 1}
            onClick={() => handlePageChange(Math.ceil(pagination.totalClients / pagination.limit) - 1)}
          >
            Last
          </button>
        </div>
      )}
    </div>
  );
};
